import { Type } from '@angular/core';
import { Routes } from '@angular/router';
import { adminAuthGuard } from './core/guards/admin-auth.guard';
import { recoverFromLazyChunkFailure } from './core/errors/lazy-chunk-recovery';

function loadComponentWithRecovery<TModule, TComponent>(
  loader: () => Promise<TModule>,
  selector: (module: TModule) => Type<TComponent>
): (() => Promise<Type<TComponent>>) {
  return () =>
    loader()
      .then(selector)
      .catch(error => {
        if (!recoverFromLazyChunkFailure(error)) {
          throw error;
        }

        return new Promise<Type<TComponent>>(() => undefined);
      });
}

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: loadComponentWithRecovery(
      () => import('./features/auth/login.page'),
      (m) => m.LoginPage
    )
  },
  {
    path: '',
    loadComponent: loadComponentWithRecovery(
      () => import('./shared/layout/shell.component'),
      (m) => m.ShellComponent
    ),
    canActivate: [adminAuthGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard'
      },
      {
        path: 'dashboard',
        data: { crumbs: [{ label: 'admin.shell.nav.dashboard' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/dashboard/dashboard.page'),
          (m) => m.DashboardPage
        )
      },
      {
        path: 'tenants/provision',
        data: { crumbs: [{ label: 'admin.shell.nav.tenants', path: '/tenants' }, { label: 'admin.tenants.provision.title' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/tenants/tenant-provision.page'),
          (m) => m.TenantProvisionPage
        )
      },
      {
        path: 'tenants',
        data: { crumbs: [{ label: 'admin.shell.nav.tenants' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/tenants/tenants-list.page'),
          (m) => m.TenantsListPage
        )
      },
      {
        path: 'packages',
        data: { crumbs: [{ label: 'admin.shell.nav.packages' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/packages/packages-list.page'),
          (m) => m.PackagesListPage
        )
      },
      {
        path: 'promo-codes',
        data: { crumbs: [{ label: 'admin.shell.nav.promoCodes' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/promo-codes/promo-codes.page'),
          (m) => m.PromoCodesPage
        )
      },
      {
        path: 'sales-links',
        data: { crumbs: [{ label: 'admin.shell.nav.salesLinks' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/sales-links/sales-links.page'),
          (m) => m.SalesLinksPage
        )
      },
      {
        path: 'leads',
        data: { crumbs: [{ label: 'admin.shell.nav.websiteLeads' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/leads/leads-list.page'),
          (m) => m.LeadsListPage
        )
      },
      {
        path: 'website-content',
        data: { crumbs: [{ label: 'admin.shell.nav.websiteContent' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/website-content/website-content.page'),
          (m) => m.WebsiteContentPage
        )
      },
      {
        path: 'operators',
        data: { crumbs: [{ label: 'admin.shell.nav.operators' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/operators/operators-list.page'),
          (m) => m.OperatorsListPage
        )
      },
      {
        path: 'tenants/:id',
        data: { crumbs: [{ label: 'admin.shell.nav.tenants', path: '/tenants' }, { label: 'admin.tenants.detail.title' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/tenants/tenant-detail.page'),
          (m) => m.TenantDetailPage
        )
      }
    ]
  }
];
